import { useRef, useState } from "react";

import { ApiError } from "../api/errors";
import type { SourceSlot } from "../api/images";
import type { ColorVariant } from "../api/types";
import {
  checkFile,
  formatBytes,
  isTerminal,
  type ReplacementStage,
  type ReplacementState,
} from "./useImageReplacement";

/** The same words the per-view control uses, because it is the same operation
 *  the same worker is doing. */
const STAGE_LABEL: Record<ReplacementStage, string> = {
  idle: "",
  preparing: "Preparing…",
  uploading: "Uploading…",
  queued: "Queued",
  processing: "Processing",
  needs_review: "Review needed",
  completed: "Completed",
  failed: "Failed",
};

/**
 * A frame colour's FIRST photographs, chosen together.
 *
 * WHY A COLOUR EVER HAS NONE. A draft does not have to begin with photographs.
 * A product imported from a workbook, or created and then filled in, is a real
 * draft whose colours exist and are simply not photographed yet — and until
 * they are, there is nothing to replace. The ordinary Front/Side/Angle controls
 * beside this one replace a view within an existing complete set; this one
 * establishes the set.
 *
 * WHY ALL THREE AT ONCE, AND NOT THREE UPLOADS IN A ROW. The listing card is
 * composed from front, side and angle together. Completing one photograph on
 * its own would ask the pipeline to compose a card from a set that does not
 * exist, so the backend refuses it — and it is right to. Asking for the three
 * together is the honest shape of the operation, not a convenience.
 *
 * It decides nothing about roles: the slots are the family's own, handed in by
 * the panel from `sourceSlotsForFamily`.
 */
export function FirstSourceSetControl({
  variant,
  slots,
  editable,
  active,
  state,
  onSubmit,
  onDismiss,
}: {
  variant: ColorVariant;
  /** Every view the set must hold, in the family's own order. */
  slots: readonly SourceSlot[];
  editable: boolean;
  /** True when this control owns the operation currently in flight. */
  active: boolean;
  state: ReplacementState;
  onSubmit: (files: { file: File; imageRole: string }[]) => void;
  onDismiss: () => void;
}) {
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  const [chosen, setChosen] = useState<Record<string, File>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const busy = active && !isTerminal(state.stage) && state.stage !== "idle";
  const complete = slots.every((slot) => chosen[slot.imageRole]);

  function choose(imageRole: string, next: File | null) {
    setErrors((prev) => ({ ...prev, [imageRole]: "" }));
    if (!next) {
      setChosen((prev) => {
        const { [imageRole]: _dropped, ...rest } = prev;
        return rest;
      });
      return;
    }
    const problem = checkFile(next);
    if (problem) {
      setErrors((prev) => ({ ...prev, [imageRole]: problem }));
      const input = inputs.current[imageRole];
      if (input) input.value = "";
      setChosen((prev) => {
        const { [imageRole]: _dropped, ...rest } = prev;
        return rest;
      });
      return;
    }
    setChosen((prev) => ({ ...prev, [imageRole]: next }));
  }

  function clear() {
    setChosen({});
    setErrors({});
    for (const input of Object.values(inputs.current)) {
      if (input) input.value = "";
    }
  }

  return (
    <section data-testid={`first-set-${variant.id}`}>
      <h3 className="section-title">
        First photographs{" "}
        {active && state.stage !== "idle" ? (
          <span className="badge badge--draft">{STAGE_LABEL[state.stage]}</span>
        ) : null}
      </h3>
      <p className="muted section-lead">
        {variant.name} has no photographs yet. A frame&apos;s images are composed
        from all three views together, so the first set is supplied at once —
        after that, each view is replaced on its own above.
      </p>

      {!editable ? null : (
        <div className="stack">
          {slots.map((slot) => {
            const inputId = `first-set-${variant.id}-${slot.imageRole}`;
            const file = chosen[slot.imageRole];
            return (
              <div key={slot.imageRole} className="row">
                <label htmlFor={inputId}>
                  {slot.label} for {variant.name}
                </label>
                <input
                  id={inputId}
                  ref={(node) => {
                    inputs.current[slot.imageRole] = node;
                  }}
                  type="file"
                  accept="image/avif,image/webp,image/png,image/jpeg"
                  disabled={busy}
                  onChange={(e) =>
                    choose(slot.imageRole, e.target.files?.[0] ?? null)
                  }
                />
                {file ? (
                  <span className="muted mono">
                    {file.name} · {formatBytes(file.size)}
                  </span>
                ) : null}
                {errors[slot.imageRole] ? (
                  <span className="field-error">{errors[slot.imageRole]}</span>
                ) : null}
              </div>
            );
          })}

          {active && state.stage === "failed" && state.error ? (
            <div className="notice notice--error" role="alert">
              {state.error instanceof ApiError
                ? state.error.userMessage
                : "The photographs could not be attached."}
            </div>
          ) : null}

          {active && state.stage === "needs_review" ? (
            <div className="notice notice--warning">
              The images were produced but flagged for review.
            </div>
          ) : null}

          {active && state.stage === "completed" ? (
            <div className="notice notice--success">Images generated.</div>
          ) : null}

          <div className="row">
            <button
              type="button"
              className="btn btn--sm btn--primary"
              disabled={!complete || busy}
              onClick={() =>
                onSubmit(
                  slots.map((slot) => ({
                    file: chosen[slot.imageRole]!,
                    imageRole: slot.imageRole,
                  })),
                )
              }
            >
              {busy ? "Sending…" : `Add ${variant.name} photographs`}
            </button>
            {complete || (active && isTerminal(state.stage)) ? (
              <button
                type="button"
                className="btn btn--sm"
                disabled={busy}
                onClick={() => {
                  clear();
                  onDismiss();
                }}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
